import React from "react";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";
import {
  PilotHostGateState,
  buildPilotSignInUrl,
  sanitiseAllowedHost,
} from "../../pilotHostGate";

interface PilotHostNoticeProps {
  gate: PilotHostGateState;
}

/**
 * Shown in place of the credential-error alert when the server refuses a sign-in because the
 * request arrived on the wrong hostname. Renders for BOTH gates, with identical copy:
 *
 *  - `PILOT_HOST_NOT_ALLOWED` (PILOT-LOGIN-COM-ONLY) — a MidasPilot outside
 *    `PilotAccess:AllowedSignInHosts`;
 *  - `HOST_NOT_ALLOWED` (HOST-POLICY-TENANT-HOME) — any user off their tenant's home surface.
 *
 * One component, one set of `pilotHostGate.*` i18n keys, on purpose: to the user both are the
 * same fact ("you are on the wrong address, here is yours"), and a second variant of this text
 * would be a translation liability with no user-visible benefit. `gate.code` says which gate
 * refused, for diagnosis only.
 *
 * Tone is deliberately INFORMATIONAL, not an error: the user typed the right password,
 * they are simply on the wrong address. Rendering the red alert here would read as
 * "your credentials are wrong", which is the one thing this is not.
 *
 * SECURITY (SEC-REQ-PH-6 / HP-8): the host is re-validated here against `^[a-z0-9.-]+$` before
 * it is interpolated into an `href`, and the user CLICKS the link — we never auto-navigate, and
 * no token exists to carry across (the sign-in was refused, so no session was created).
 */
const PilotHostNotice = ({ gate }: PilotHostNoticeProps) => {
  const { translate } = useTranslation("account");

  const host = sanitiseAllowedHost(gate.allowedHost);

  // Fail-closed config (an empty PilotAccess:AllowedSignInHosts, or an empty HostPolicy list for
  // the tenant's home surface) — the server names no host, so there is nothing to link to. Say so
  // plainly instead of rendering a dead link.
  if (!host) {
    return (
      <div
        className="rounded-lg px-4 py-3 text-sm"
        style={{
          background: dt.blueSoft,
          border: `1px solid ${dt.cardBorder}`,
          borderRadius: dt.radiusSm,
          color: dt.textPrimary,
        }}
        role="status"
      >
        {translate("pilotHostGate.messageNoHost")}
      </div>
    );
  }

  const url = buildPilotSignInUrl(host);
  const displayHost = `https://${host}`;

  return (
    <div
      className="rounded-lg px-4 py-3 text-sm"
      style={{
        background: dt.blueSoft,
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: dt.radiusSm,
        color: dt.textPrimary,
      }}
      role="status"
    >
      <div className="break-all">{translate("pilotHostGate.message", { host: displayHost })}</div>

      <a
        href={url}
        rel="noopener"
        className="mt-2 inline-block break-all underline"
        style={{ color: dt.blue }}
      >
        {translate("pilotHostGate.linkLabel", { host: displayHost })}
      </a>
    </div>
  );
};

export default PilotHostNotice;
